import { mapTournamentPlan, TournamentPlanDto } from 'modules/organizer/domain/data-mappers/tournament-plan.data-mapper';
import { mapWallet, WalletDto } from 'modules/organizer/domain/data-mappers/wallet.data-mapper';
import { mapWalletTopup, WalletTopupDto } from 'modules/organizer/domain/data-mappers/wallet-topup.data-mapper';
import { appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { Wallet } from 'modules/organizer/domain/interfaces/wallet';
import { WalletTopup, WalletTopupPaymentMethod, WalletTopupStatus } from 'modules/organizer/domain/interfaces/wallet-topup';
import { baseApi } from 'shared/api/base-api';

const myWalletQuery = `#graphql
  query MyWallet($tournamentId: Int!) {
    myWallet(tournamentId: $tournamentId) {
      id
      balanceInMinorUnits
    }
  }
`;

const tournamentPlansQuery = `#graphql
  query TournamentPlans {
    tournamentPlans {
      id
      name
      priceInMinorUnits
      period
      createdAt
      features {
        id
        feature
      }
    }
  }
`;

const walletTopupQuery = `#graphql
  query WalletTopup($id: Int!) {
    walletTopup(id: $id) {
      id
      amountInMinorUnits
      qrPayload
      paymentUrl
      paymentMethod
      status
      tbankPaymentId
      errorMessage
      paidSubscription {
        id
      }
    }
  }
`;

const createWalletTopupMutation = `#graphql
  mutation CreateWalletTopup($input: CreateWalletTopupInput!) {
    createWalletTopup(input: $input) {
      id
      amountInMinorUnits
      qrPayload
      paymentUrl
      paymentMethod
      status
      tbankPaymentId
      errorMessage
      paidSubscription {
        id
      }
    }
  }
`;

const simulateWalletTopupSbpPayMutation = `#graphql
  mutation SimulateWalletTopupSbpPay($input: SimulateWalletTopupSbpPayInput!) {
    simulateWalletTopupSbpPay(input: $input) {
      id
      amountInMinorUnits
      qrPayload
      paymentUrl
      paymentMethod
      status
      tbankPaymentId
      errorMessage
      paidSubscription {
        id
      }
    }
  }
`;

const buyPlanMutation = `#graphql
  mutation BuyPlan($input: BuyPlanInput!) {
    buyPlan(input: $input) {
      id
    }
  }
`;

const setTournamentPlanFeaturesMutation = `#graphql
  mutation SetTournamentPlanFeatures($input: SetTournamentPlanFeaturesInput!) {
    setTournamentPlanFeatures(input: $input) {
      id
      name
      priceInMinorUnits
      period
      createdAt
      features {
        id
        feature
      }
    }
  }
`;

export interface CreateWalletTopupInput {
  tournamentId: number;
  amountInMinorUnits: number;
  paymentMethod: WalletTopupPaymentMethod;
  email: string;
}

export interface BuyPlanInput {
  tournamentId: number;
  planId: number;
  switchPlan: boolean;
  createRecurring: boolean;
}

export interface SimulateWalletTopupSbpPayInput {
  id: number;
  status: WalletTopupStatus;
}

export interface SetTournamentPlanFeaturesInput {
  planId: number;
  features: appFeature[];
}

const billingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMyWallet: build.query<Wallet, number>({
      query: (tournamentId) => ({ query: myWalletQuery, variables: { tournamentId } }),
      transformResponse: (raw: { myWallet: WalletDto }) => mapWallet(raw.myWallet),
      providesTags: ['Wallet'],
    }),
    getTournamentPlans: build.query<TournamentPlan[], void>({
      query: () => ({ query: tournamentPlansQuery }),
      transformResponse: (raw: { tournamentPlans: TournamentPlanDto[] }) => raw.tournamentPlans.map(mapTournamentPlan),
      providesTags: ['TournamentPlan'],
    }),
    getWalletTopup: build.query<WalletTopup | null, number>({
      query: (id) => ({ query: walletTopupQuery, variables: { id } }),
      transformResponse: (raw: { walletTopup: WalletTopupDto | null }) =>
        raw.walletTopup ? mapWalletTopup(raw.walletTopup) : null,
    }),
    createWalletTopup: build.mutation<WalletTopup, CreateWalletTopupInput>({
      query: (input) => ({
        query: createWalletTopupMutation,
        variables: { input },
      }),
      transformResponse: (raw: { createWalletTopup: WalletTopupDto }) => mapWalletTopup(raw.createWalletTopup),
    }),
    simulateWalletTopupSbpPay: build.mutation<WalletTopup, SimulateWalletTopupSbpPayInput>({
      query: (input) => ({
        query: simulateWalletTopupSbpPayMutation,
        variables: { input },
      }),
      transformResponse: (raw: { simulateWalletTopupSbpPay: WalletTopupDto }) =>
        mapWalletTopup(raw.simulateWalletTopupSbpPay),
    }),
    buyPlan: build.mutation<{ id: number }, BuyPlanInput>({
      query: (input) => ({
        query: buyPlanMutation,
        variables: { input },
      }),
      transformResponse: (raw: { buyPlan: { id: number } }) => raw.buyPlan,
      invalidatesTags: (_res, _err, { tournamentId }) => ['Wallet', { type: 'Organizer', id: tournamentId }],
    }),
    setTournamentPlanFeatures: build.mutation<TournamentPlan, SetTournamentPlanFeaturesInput>({
      query: (input) => ({
        query: setTournamentPlanFeaturesMutation,
        variables: { input },
      }),
      transformResponse: (raw: { setTournamentPlanFeatures: TournamentPlanDto }) =>
        mapTournamentPlan(raw.setTournamentPlanFeatures),
      invalidatesTags: ['TournamentPlan'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMyWalletQuery,
  useGetTournamentPlansQuery,
  useGetWalletTopupQuery,
  useCreateWalletTopupMutation,
  useSimulateWalletTopupSbpPayMutation,
  useBuyPlanMutation,
  useSetTournamentPlanFeaturesMutation,
} = billingApi;

export default billingApi;
